import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  #requests = new Map();
  #generation = 0;
  #renderingNext = null;
  #filterPending = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();
    scrollContainerMediaQuery.addEventListener('change', this.#resetObserver);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.#generation;
    this.pages.clear();
    this.#requests.clear();
    this.#renderingNext = null;
    this.#filterPending = false;
    scrollContainerMediaQuery.removeEventListener('change', this.#resetObserver);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.#filterPending) this.#observeViewMore();
  }

  #resetObserver = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    if (!this.#filterPending) this.#observeViewMore();
  };

  #observeViewMore() {
    const { viewMoreNext } = this.refs;
    this.#syncNextLink();

    if (!viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMoreNext } = this.refs;

              if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          root: getIntersectionRoot(),
          rootMargin: '100px 0px 1200px 0px',
        }
      );
    }

    this.infinityScrollObserver.disconnect();

    if (viewMoreNext && !viewMoreNext.hidden) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchPage() {
    const page = this.#getNextPage();
    if (!page || !this.#shouldUsePage(page) || this.pages.has(page.page)) return;
    const generation = this.#generation;
    const key = page.url.href;
    let request = this.#requests.get(key);
    if (!request) {
      request = sectionRenderer.getSectionHTML(this.sectionId, true, page.url);
      this.#requests.set(key, request);
    }
    try {
      const html = await request;
      if (generation === this.#generation && this.isConnected) this.pages.set(page.page, html);
    } finally {
      if (this.#requests.get(key) === request) this.#requests.delete(key);
    }
  }

  /** Keep the buffer and the non-JavaScript fallback in sync with appended pages. */
  #syncNextLink() {
    const next = this.#getNextPage();
    const marker = this.querySelector('[ref="viewMoreNext"]');
    if (!(marker instanceof HTMLElement)) return;
    marker.hidden = !next || !this.#shouldUsePage(next);
    const link = marker.querySelector('a');
    if (link && next) link.href = next.url.href;
  }

  async #renderNextPage() {
    if (this.#renderingNext || this.#filterPending) return;
    const grid = this.refs.grid;
    const nextPage = this.#getNextPage();
    if (!grid || !nextPage || !this.#shouldUsePage(nextPage)) return;
    const job = { generation: this.#generation };
    this.#renderingNext = job;
    try {
      await this.#fetchPage();
      if (!this.isConnected || job.generation !== this.#generation || grid !== this.refs.grid) return;
      const cards = this.#getGridForPage(nextPage.page);
      if (!cards?.length) return;
      grid.append(...cards);
      this.#aspectRatioHelper?.processNewElements();
      this.#syncNextLink();
    } catch (error) {
      console.warn('[paginated-list] Could not load the next page:', error);
    } finally {
      if (this.#renderingNext === job) this.#renderingNext = null;
    }
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    // Appending a page can precede Component's deferred ref refresh.
    const cards = this.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
    const targetCard = cards[cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    // Match the crawlable filter URLs emitted by the theme; literal plus signs remain %2B.
    url.search = url.search.replace(/\+/g, '%20');
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    // Keep images inert until the requested cards enter the live grid.
    const template = document.createElement('template');
    template.innerHTML = pageHTML;
    const gridElement = template.content.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    const generation = ++this.#generation;
    this.pages.clear();
    this.#requests.clear();
    this.#renderingNext = null;
    this.#filterPending = true;
    this.infinityScrollObserver?.disconnect();

    // Standard collection/search events carry the actual section render promise.
    // Waiting for it also handles filters with an unchanged number of pages.
    const renderPromise = /** @type {Event & {promise?: Promise<unknown>}} */ (event).promise;
    Promise.resolve(renderPromise).catch(() => {}).then(() => {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#filterPending = false;
      super.updatedCallback();
      this.#observeViewMore();
    });
  };
}
